import { useParams, Link } from 'react-router';
import { useAssessmentDetail } from '@/api/competency';
import { BackButton } from '@/components/shared/back-button';
import { LoadingSpinner } from '@/components/shared/loading-spinner';
import { ErrorMessage } from '@/components/shared/error-message';
import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/formatters';
import { CheckCircle, XCircle, AlertTriangle, ArrowRight, ClipboardList } from 'lucide-react';
import {
  NEXT_ACTIVITY_LINK,
  TYPE_BADGES,
  TYPE_LABELS,
  STATUS_BADGES,
  STATUS_LABELS,
  CA_STATUS_BADGES,
  CA_STATUS_LABELS,
} from './assessment-detail.labels';

export function AssessmentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const assessmentId = Number(id) || 0;

  const { data: assessment, isLoading, error } = useAssessmentDetail(assessmentId);

  if (isLoading) return <LoadingSpinner />;
  if (error || !assessment) {
    return <ErrorMessage message="No se pudo cargar el detalle de la evaluación." />;
  }

  const evaluatorName = assessment.assessor
    ? `${assessment.assessor.first_name} ${assessment.assessor.last_name}`
    : null;

  const assessedName = assessment.assessed
    ? `${assessment.assessed.first_name} ${assessment.assessed.last_name}`
    : null;

  return (
    <div className="space-y-6">
      <BackButton fallback="/competency" />

      {/* Header */}
      <div className="rounded-xl border border-border bg-card p-6">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={cn(
              'rounded-full px-2 py-0.5 text-xs font-medium',
              TYPE_BADGES[assessment.assessment_type],
            )}
          >
            {TYPE_LABELS[assessment.assessment_type] ?? assessment.assessment_type}
          </span>
          <span
            className={cn(
              'rounded-full px-2 py-0.5 text-xs font-medium',
              STATUS_BADGES[assessment.status],
            )}
          >
            {STATUS_LABELS[assessment.status]}
          </span>
        </div>
        {assessedName && (
          <p className="mt-3 text-sm text-muted-foreground">
            <span className="font-medium text-foreground">Evaluado:</span> {assessedName}
          </p>
        )}
        {evaluatorName && (
          <p className="mt-1 text-sm text-muted-foreground">
            <span className="font-medium text-foreground">Responsable de la evaluación:</span>{' '}
            {evaluatorName}
          </p>
        )}
      </div>

      {/* Next step (F5b/#6) — what the colaborador must do after being notified */}
      {(() => {
        const activities =
          assessment.next_activities && assessment.next_activities.length > 0
            ? assessment.next_activities
            : assessment.next_activity_type && assessment.next_activity_id != null
              ? [{ type: assessment.next_activity_type, activity_id: assessment.next_activity_id }]
              : [];
        if (!assessment.instructions && activities.length === 0) return null;
        return (
          <div className="rounded-xl border border-primary/30 bg-primary/5 p-6">
            <div className="flex items-center gap-2">
              <ClipboardList className="h-4 w-4 text-primary" />
              <h2 className="text-sm font-medium text-foreground">
                {activities.length > 1 ? 'Siguientes pasos' : 'Siguiente paso'}
              </h2>
            </div>
            {assessment.instructions && (
              <p className="mt-2 whitespace-pre-line text-sm text-muted-foreground">
                {assessment.instructions}
              </p>
            )}
            <ul className="mt-3 space-y-3">
              {activities.map((a, i) => {
                const link = NEXT_ACTIVITY_LINK[a.type];
                if (!link) return null;
                const instr = 'instructions' in a ? a.instructions : undefined;
                return (
                  <li key={i}>
                    {instr && <p className="mb-1 text-sm text-muted-foreground">{instr}</p>}
                    <Link
                      to={link.to(a.activity_id)}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                    >
                      {link.label}
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })()}

      {/* Score section */}
      {assessment.status === 'completed' && assessment.score != null && (
        <div className="rounded-xl border border-border bg-card p-6">
          <h2 className="text-sm font-medium text-foreground">Resultado</h2>
          <div className="mt-3 flex items-center gap-4">
            <span className="text-4xl font-bold text-card-foreground">{assessment.score}%</span>
            {assessment.passed ? (
              <span className="flex items-center gap-1.5 text-lg font-semibold text-green-600">
                <CheckCircle className="h-6 w-6" /> Aprobado
              </span>
            ) : (
              <span className="flex items-center gap-1.5 text-lg font-semibold text-red-600">
                <XCircle className="h-6 w-6" /> Reprobado
              </span>
            )}
          </div>
        </div>
      )}

      {/* Criteria table */}
      {assessment.criteria?.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-6">
          <h2 className="text-sm font-medium text-foreground">Criterios de Evaluación</h2>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="pb-2 pr-4 font-medium text-muted-foreground">Criterio</th>
                  <th className="pb-2 pr-4 text-center font-medium text-muted-foreground">
                    Puntaje
                  </th>
                  <th className="pb-2 font-medium text-muted-foreground">Comentarios</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {assessment.criteria.map((criterion, idx) => (
                  <tr key={idx}>
                    <td className="py-3 pr-4 font-medium text-card-foreground">{criterion.name}</td>
                    <td className="py-3 pr-4 text-center text-card-foreground">
                      {criterion.score} / {criterion.max_score}
                    </td>
                    <td className="py-3 text-muted-foreground">{criterion.comments ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Comments */}
      {assessment.overall_comments && (
        <div className="rounded-xl border border-border bg-card p-6">
          <h2 className="text-sm font-medium text-foreground">Comentarios Generales</h2>
          <p className="mt-2 whitespace-pre-line text-sm text-muted-foreground">
            {assessment.overall_comments}
          </p>
        </div>
      )}

      {/* Dates */}
      <div className="rounded-xl border border-border bg-card p-6">
        <h2 className="text-sm font-medium text-foreground">Fechas</h2>
        <div className="mt-3 space-y-1 text-sm">
          <p>
            <span className="font-medium text-foreground">Creada:</span>{' '}
            <span className="text-muted-foreground">{formatDate(assessment.created_at)}</span>
          </p>
          {assessment.completed_at && (
            <p>
              <span className="font-medium text-foreground">Completada:</span>{' '}
              <span className="text-muted-foreground">{formatDate(assessment.completed_at)}</span>
            </p>
          )}
        </div>
      </div>

      {/* Related corrective actions */}
      {assessment.corrective_actions && assessment.corrective_actions.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-6">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            <h2 className="text-sm font-medium text-foreground">Acciones Correctivas Asociadas</h2>
          </div>
          <div className="mt-4 space-y-3">
            {assessment.corrective_actions.map((ca) => (
              <Link
                key={ca.id}
                to={`/competency/corrective-actions/${ca.id}`}
                className="block rounded-lg border border-border p-4 transition-colors hover:bg-accent/50"
              >
                <p className="text-sm text-card-foreground">{ca.description}</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <span
                    className={cn(
                      'rounded-full px-2 py-0.5 text-xs font-medium',
                      CA_STATUS_BADGES[ca.status],
                    )}
                  >
                    {CA_STATUS_LABELS[ca.status]}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
